import React, { useCallback, useEffect, useState } from "react";
import { PluginStore } from "graylog-web-plugin/plugin";
import cloneDeep from "lodash/cloneDeep";
import { useQuery } from "@tanstack/react-query";
import { useStore } from "stores/connect";
import EventDefinitionPriorityEnum from "logic/alerts/EventDefinitionPriorityEnum";
import { ConfirmLeaveDialog, Spinner } from "components/common";
import { ConfigurationsActions } from "stores/configurations/ConfigurationsStore";
import fetch from "logic/rest/FetchProvider";
import { qualifyUrl, getPathnameWithoutId } from "util/URLUtils";
import { defaultOnError } from "util/conditional/onError";
import { EventDefinitionsActions } from "stores/event-definitions/EventDefinitionsStore";
import { EventNotificationsActions, EventNotificationsStore } from "stores/event-notifications/EventNotificationsStore";
import { CurrentUserStore } from "stores/users/CurrentUserStore";
import useCurrentUser from "hooks/useCurrentUser";
import useEventDefinitionConfigFromLocalStorage from "components/event-definitions/hooks/useEventDefinitionConfigFromLocalStorage";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import useLocation from "routing/useLocation";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import useScopePermissions from "hooks/useScopePermissions";
import EventDefinitionForm, { getStepKeys } from "./EventDefinitionForm";
import useEventDefinitionMutations from "../hooks/useEventDefinitionMutations";
const fetchNotifications = () => {
  EventNotificationsActions.listAll();
};
const getConditionPlugin = (edType) => {
  if (edType === void 0) {
    return {};
  }
  return PluginStore.exports("eventDefinitionTypes").find((eventDefinitionType) => eventDefinitionType.type === edType) || {};
};
const EventDefinitionFormContainer = ({
  action = "edit",
  eventDefinition: eventDefinitionInitial = {
    title: "",
    description: "",
    priority: EventDefinitionPriorityEnum.MEDIUM,
    // @ts-ignore
    config: {},
    field_spec: {},
    key_spec: [],
    notification_settings: {
      grace_period_ms: 3e5,
      // Defaults to system setting for notification backlog size
      backlog_size: null
    },
    notifications: [],
    alert: false
  },
  formControls = void 0,
  initialStep = "event-details",
  onCancel = void 0,
  onChangeStep = void 0,
  onEventDefinitionChange = () => {
  },
  onSubmit = void 0
}) => {
  var _a;
  const [activeStep, setActiveStep] = useState(initialStep);
  const [eventDefinition, setEventDefinition] = useState(eventDefinitionInitial);
  const [validation, setValidation] = useState({ errors: {} });
  const [eventsClusterConfig, setEventsClusterConfig] = useState(void 0);
  const [isDirty, setIsDirty] = useState(false);
  const { configFromLocalStorage, hasLocalStorageConfig } = useEventDefinitionConfigFromLocalStorage();
  const { loadingScopePermissions, scopePermissions } = useScopePermissions(eventDefinition);
  const { createEventDefinition } = useEventDefinitionMutations();
  const { data: entityTypes } = useQuery({
    queryKey: ["event-definitions", "entity-types"],
    queryFn: () => defaultOnError(
      fetch("GET", qualifyUrl("/events/entity_types")),
      "Loading event definition entity types failed with status",
      "Could not load event definition entity types"
    )
  });
  const notifications = useStore(EventNotificationsStore);
  const currentUser = useCurrentUser();
  const { pathname } = useLocation();
  const sendTelemetry = useSendTelemetry();
  const isNew = action === "create";
  const conditionPlugin = getConditionPlugin(eventDefinition.config.type);
  const hideFieldsStep = (_a = conditionPlugin == null ? void 0 : conditionPlugin.hideFieldsStep) != null ? _a : false;
  const currentStepKeys = getStepKeys(isNew, hideFieldsStep);
  const isLoading = !entityTypes || !notifications.all || !eventsClusterConfig;
  const defaults = { default_backlog_size: eventsClusterConfig == null ? void 0 : eventsClusterConfig.events_notification_default_backlog };
  const fetchClusterConfig = useCallback(() => {
    ConfigurationsActions.listEventsClusterConfig().then((config) => setEventsClusterConfig(config));
  }, []);
  const handleChange = useCallback(
    (key, value) => {
      setEventDefinition((prev) => ({ ...prev, [key]: value }));
      onEventDefinitionChange({ ...eventDefinition, [key]: value });
      setIsDirty(true);
    },
    [eventDefinition, onEventDefinitionChange, setEventDefinition, setIsDirty]
  );
  useEffect(() => {
    fetchClusterConfig();
    fetchNotifications();
    if (hasLocalStorageConfig) {
      const localStorageConditionPlugin = getConditionPlugin(configFromLocalStorage.type);
      const defaultConfig = (localStorageConditionPlugin == null ? void 0 : localStorageConditionPlugin.defaultConfig) || {};
      setEventDefinition((cur) => {
        const cloned = cloneDeep(cur);
        return {
          ...cloned,
          config: {
            ...defaultConfig,
            ...cloned.config,
            ...configFromLocalStorage
          }
        };
      });
    }
  }, [configFromLocalStorage, fetchClusterConfig, hasLocalStorageConfig]);
  const handleSubmitSuccessResponse = () => {
    setIsDirty(false);
    CurrentUserStore.update(currentUser.username);
    onSubmit();
  };
  const showValidationErrors = (errors) => {
    setValidation(errors);
    setActiveStep(currentStepKeys[currentStepKeys.length - 1]);
  };
  const handleSubmitFailureResponse = (errorResponse) => {
    const { body } = errorResponse.additional;
    if (errorResponse.status === 400) {
      if (body && body.failed) {
        showValidationErrors(body);
        return;
      }
      if (body.type && body.type === "ApiError") {
        if (body.message.includes("org.graylog.events.conditions.Expression") || body.message.includes("org.graylog.events.conditions.Expr") || body.message.includes("org.graylog.events.processor.aggregation.AggregationSeries")) {
          showValidationErrors({
            errors: { conditions: ["Aggregation condition is not valid"] }
          });
          return;
        }
        if (body.message.includes("embryonic")) {
          showValidationErrors({
            errors: { query_parameters: ["Query parameters must be declared"] }
          });
        }
      }
    }
  };
  const handleSubmit = () => {
    setIsDirty(false);
    if (action === "create") {
      sendTelemetry(TELEMETRY_EVENT_TYPE.EVENTDEFINITION_SUMMARY.CREATE_CLICKED, {
        app_pathname: getPathnameWithoutId(pathname),
        app_section: "new-event-definition",
        app_action_value: "create-event-definition-button"
      });
      createEventDefinition(eventDefinition).then(handleSubmitSuccessResponse, handleSubmitFailureResponse);
    } else {
      sendTelemetry(TELEMETRY_EVENT_TYPE.EVENTDEFINITION_SUMMARY.UPDATE_CLICKED, {
        app_pathname: getPathnameWithoutId(pathname),
        app_section: "edit-event-definition",
        app_action_value: "update-event-definition-button"
      });
      EventDefinitionsActions.update(eventDefinition.id, eventDefinition).then(
        handleSubmitSuccessResponse,
        handleSubmitFailureResponse
      );
    }
  };
  const handleCancel = () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.EVENTDEFINITION_SUMMARY.CANCEL_CLICKED, {
      app_pathname: getPathnameWithoutId(pathname),
      app_section: action === "create" ? "new-event-definition" : "edit-event-definition",
      app_action_value: "cancel-button"
    });
    onCancel();
  };
  const changeStep = (step) => {
    onChangeStep == null ? void 0 : onChangeStep(step);
    setActiveStep(step);
  };
  if (isLoading || loadingScopePermissions) {
    return /* @__PURE__ */ React.createElement(Spinner, { text: "Loading Event information..." });
  }
  return /* @__PURE__ */ React.createElement(React.Fragment, null, isDirty && /* @__PURE__ */ React.createElement(ConfirmLeaveDialog, { question: "Do you really want to abandon this page and lose your changes? This action cannot be undone." }), /* @__PURE__ */ React.createElement(
    EventDefinitionForm,
    {
      action,
      canEdit: scopePermissions.is_mutable,
      currentUser,
      defaults,
      activeStep,
      entityTypes,
      eventDefinition,
      formControls,
      notifications: notifications.all,
      onCancel: handleCancel,
      onChange: handleChange,
      onChangeStep: changeStep,
      onSubmit: handleSubmit,
      validation,
      hideFieldsStep
    }
  ));
};
export default EventDefinitionFormContainer;
